import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import { DateField } from '../components/dropdown';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import SyncIcon from '@mui/icons-material/Sync';
import {
  DataTableCard,
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
} from '../components/table';
import { Chip } from '../components/chip';
import { CARD_SX } from '../styles/designSystem';
import {
  SYNC_ENTITIES,
  runFullSync,
  runEntityResync,
  runReconcile,
} from '../services/rsSyncService';
import type {
  SyncResponse,
  SyncResult,
  ReconcileResponse,
  FullSyncParams,
  ResyncParams,
} from '../services/rsSyncService';
import { MetricsPage } from './MetricsPage';

type TabView = 'sync' | 'metrics';
type ResultType = 'sync' | 'reconcile';

interface SyncResultState {
  type: ResultType;
  data: SyncResponse | ReconcileResponse;
  dryRun: boolean;
}

// Uppercase section header, design-system label style (sm4rt-web-ui §2).
const SECTION_LABEL_SX = {
  textTransform: 'uppercase',
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '0.05em',
  color: '#646464',
} as const;

export const RepairShoprSyncPage: React.FC = () => {
  const [tabView, setTabView] = useState<TabView>('sync');

  // Running state
  const [running, setRunning] = useState<'sync' | 'resync' | 'reconcile' | false>(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SyncResultState | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

  // Full Sync form
  const [syncFrom, setSyncFrom] = useState(today);
  const [syncDryRun, setSyncDryRun] = useState(true);
  const [syncUpdateLimit, setSyncUpdateLimit] = useState(300);

  // Resync form
  const [resyncEntity, setResyncEntity] = useState('products');
  const [resyncFrom, setResyncFrom] = useState(today);
  const [resyncTo, setResyncTo] = useState(today);
  const [resyncId, setResyncId] = useState('');
  const [resyncDryRun, setResyncDryRun] = useState(false);
  const [resyncEnrich, setResyncEnrich] = useState(false);
  const [resyncUpdateLimit, setResyncUpdateLimit] = useState(300);
  const [resyncFromPage, setResyncFromPage] = useState(1);

  const handleFullSync = async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setRunning('sync');
    setError(null);
    setResult(null);

    try {
      const params: FullSyncParams = {
        from: syncFrom || undefined,
        dryRun: syncDryRun || undefined,
        updateLimit: syncUpdateLimit !== 300 ? syncUpdateLimit : undefined,
      };
      const data = await runFullSync(params, controller.signal);
      setResult({ type: 'sync', data, dryRun: syncDryRun });
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Full sync failed');
    } finally {
      setRunning(false);
    }
  };

  const handleResync = async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setRunning('resync');
    setError(null);
    setResult(null);

    try {
      const params: ResyncParams = {
        from: resyncFrom || undefined,
        to: resyncTo || undefined,
        dryRun: resyncDryRun || undefined,
        enrich: resyncEnrich || undefined,
        updateLimit: resyncUpdateLimit !== 300 ? resyncUpdateLimit : undefined,
        fromPage: resyncFromPage > 1 ? resyncFromPage : undefined,
        id: resyncId || undefined,
      };
      const data = await runEntityResync(resyncEntity, params, controller.signal);
      setResult({ type: 'sync', data, dryRun: resyncDryRun });
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Resync failed');
    } finally {
      setRunning(false);
    }
  };

  const handleReconcile = async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setRunning('reconcile');
    setError(null);
    setResult(null);

    try {
      const data = await runReconcile(controller.signal);
      setResult({ type: 'reconcile', data, dryRun: false });
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Reconcile failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Box sx={{ p: '10px 16px 24px' }}>
      <Tabs
        value={tabView}
        onChange={(_, v) => setTabView(v)}
        sx={{ mb: 1, minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5, textTransform: 'none', fontSize: '0.85rem' } }}
      >
        <Tab label="Sync Operations" value="sync" />
        <Tab label="Metrics" value="metrics" />
      </Tabs>

      {tabView === 'metrics' && <MetricsPage />}

      {tabView === 'sync' && (
        <>
          {/* Full Sync */}
          <Box sx={[CARD_SX, { p: 2, pb: 3, mb: 1 }]}>
            <Typography sx={{ ...SECTION_LABEL_SX, mb: 1.5 }}>
              Full Sync
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Helper hangs below the field so it doesn't throw off the row's centring. */}
              <Box sx={{ width: 170, '& .date-field__helper': { position: 'absolute', top: '100%', left: 0 } }}>
                <DateField
                  label="From Date"
                  value={syncFrom || null}
                  onChange={(iso) => setSyncFrom(iso ?? '')}
                  helperText="Empty = today only"
                />
              </Box>
              <TextField
                label="Update Limit"
                type="number"
                size="small"
                value={syncUpdateLimit}
                onChange={(e) => setSyncUpdateLimit(Number(e.target.value))}
                sx={{ width: 120 }}
              />
              <FormControlLabel
                control={<Switch checked={syncDryRun} onChange={(e) => setSyncDryRun(e.target.checked)} size="small" />}
                label="Dry Run"
              />
              <Button
                variant="contained"
                size="small"
                startIcon={running ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                onClick={handleFullSync}
                disabled={running}
              >
                Run Sync
              </Button>
            </Box>
          </Box>

          {/* Resync Entity */}
          <Box sx={[CARD_SX, { p: 2, mb: 1 }]}>
            <Typography sx={{ ...SECTION_LABEL_SX, mb: 1.5 }}>
              Resync Entity
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                label="Entity"
                select
                size="small"
                value={resyncEntity}
                onChange={(e) => setResyncEntity(e.target.value)}
                sx={{ minWidth: 200 }}
              >
                {SYNC_ENTITIES.map((e) => (
                  <MenuItem key={e} value={e}>{e}</MenuItem>
                ))}
              </TextField>
              <div style={{ width: 170 }}>
                <DateField label="From Date" value={resyncFrom || null} onChange={(iso) => setResyncFrom(iso ?? '')} />
              </div>
              <div style={{ width: 170 }}>
                <DateField label="To Date" value={resyncTo || null} onChange={(iso) => setResyncTo(iso ?? '')} />
              </div>
              <TextField
                label="Record ID"
                size="small"
                value={resyncId}
                onChange={(e) => setResyncId(e.target.value)}
                placeholder="Single record"
                sx={{ width: 140 }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mt: 1.5 }}>
              <TextField
                label="Update Limit"
                type="number"
                size="small"
                value={resyncUpdateLimit}
                onChange={(e) => setResyncUpdateLimit(Number(e.target.value))}
                sx={{ width: 120 }}
              />
              <TextField
                label="From Page"
                type="number"
                size="small"
                value={resyncFromPage}
                onChange={(e) => setResyncFromPage(Number(e.target.value))}
                sx={{ width: 110 }}
              />
              <FormControlLabel
                control={<Switch checked={resyncDryRun} onChange={(e) => setResyncDryRun(e.target.checked)} size="small" />}
                label="Dry Run"
              />
              <FormControlLabel
                control={<Switch checked={resyncEnrich} onChange={(e) => setResyncEnrich(e.target.checked)} size="small" />}
                label="Enrich"
              />
              <Button
                variant="contained"
                size="small"
                startIcon={running ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                onClick={handleResync}
                disabled={running}
              >
                Resync
              </Button>
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 1.5, maxWidth: 760, lineHeight: 1.5 }}
            >
              <strong>Enrich</strong> re-fetches each record from RepairShopr's full detail
              endpoint instead of the lighter list feed — slower (one API call per record), so
              leave it off for routine syncs and enable it when back-filling detail.
              {resyncEntity === 'invoices' && (
                <>
                  {' '}For invoices this populates fields the list feed omits:{' '}
                  <strong>line items</strong>, <strong>balance due</strong>, and{' '}
                  <strong>hardware cost</strong>.
                </>
              )}
            </Typography>
          </Box>

          {/* Reconcile */}
          <Box sx={[CARD_SX, { p: 2, mb: 1 }]}>
            <Typography sx={{ ...SECTION_LABEL_SX, mb: 1.5 }}>
              Reconcile
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button
                variant="contained"
                size="small"
                startIcon={running ? <CircularProgress size={16} color="inherit" /> : <CompareArrowsIcon />}
                onClick={handleReconcile}
                disabled={running}
              >
                Run Reconcile
              </Button>
              <Typography variant="caption" color="text.secondary">
                Compares record counts between RepairShopr and Dataverse
              </Typography>
            </Box>
          </Box>

          {/* Running indicator */}
          {running && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {running === 'reconcile'
                ? 'Running reconcile...'
                : 'Operation in progress — this may take a minute...'}
            </Alert>
          )}

          {/* Error */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Results */}
          {result && result.type === 'sync' && <SyncResultsTable result={result} />}
          {result && result.type === 'reconcile' && <ReconcileResultsTable result={result} />}
        </>
      )}
    </Box>
  );
};

const SyncResultsTable: React.FC<{ result: SyncResultState }> = ({ result }) => {
  const raw = result.data as unknown as Record<string, unknown>;
  // Normalize: the response may arrive as a full SyncResponse or as a single SyncResult
  const data: SyncResponse = 'results' in raw && Array.isArray(raw.results)
    ? raw as unknown as SyncResponse
    : { results: [raw as unknown as SyncResult], elapsed_seconds: (raw.elapsed_seconds as number) ?? 0, rs_api_calls: (raw.rs_api_calls as number) ?? 0, dv_api_calls: (raw.dv_api_calls as number) ?? 0 };
  return (
    <DataTableCard>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: '10px 12px', borderBottom: '0.5px solid #e2eaf3' }}>
        <Typography sx={SECTION_LABEL_SX}>Results</Typography>
        {result.dryRun && <Chip tone="info">DRY RUN</Chip>}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {(data.elapsed_seconds ?? 0).toFixed(1)}s &middot; RS calls: {data.rs_api_calls ?? 0} &middot; DV calls: {data.dv_api_calls ?? 0}
        </Typography>
      </Box>
      <DataTable>
        <thead>
          <tr>
            <DataTableHeadCell>Entity</DataTableHeadCell>
            <DataTableHeadCell align="right">Updated</DataTableHeadCell>
            <DataTableHeadCell align="right">Errors</DataTableHeadCell>
            <DataTableHeadCell align="right">Source Total</DataTableHeadCell>
            <DataTableHeadCell align="right">Target Total</DataTableHeadCell>
            <DataTableHeadCell align="right">Duration</DataTableHeadCell>
            <DataTableHeadCell>Summary</DataTableHeadCell>
          </tr>
        </thead>
        <tbody>
          {data.results.map((r) => (
            <DataTableRow key={r.entity}>
              <DataTableCell bold>{r.entity}</DataTableCell>
              <DataTableCell align="right">{r.updatedRecordCount ?? r.updated}</DataTableCell>
              <DataTableCell align="right">
                {(r.failedRecordCount || r.errors) > 0 ? (
                  <Chip tone="error">{r.failedRecordCount || r.errors}</Chip>
                ) : (
                  0
                )}
              </DataTableCell>
              <DataTableCell align="right">{r.sourceTotalRecordCount ?? '—'}</DataTableCell>
              <DataTableCell align="right">{r.targetTotalRecordCount ?? '—'}</DataTableCell>
              <DataTableCell align="right">{(r.elapsed_seconds ?? 0).toFixed(1)}s</DataTableCell>
              <DataTableCell muted>
                <Typography variant="body2" noWrap sx={{ maxWidth: 300, fontSize: 13 }}>
                  {r.summaryInfo?.replace(/<br\s*\/?>/gi, ' · ') || '—'}
                </Typography>
              </DataTableCell>
            </DataTableRow>
          ))}
        </tbody>
      </DataTable>
    </DataTableCard>
  );
};

const ReconcileResultsTable: React.FC<{ result: SyncResultState }> = ({ result }) => {
  const data = result.data as ReconcileResponse;
  return (
    <DataTableCard>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: '10px 12px', borderBottom: '0.5px solid #e2eaf3' }}>
        <Typography sx={SECTION_LABEL_SX}>Reconcile Results</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {(data.elapsed_seconds ?? 0).toFixed(1)}s &middot; RS calls: {data.rs_api_calls ?? 0} &middot; DV calls: {data.dv_api_calls ?? 0}
        </Typography>
      </Box>
      <DataTable>
        <thead>
          <tr>
            <DataTableHeadCell>Entity</DataTableHeadCell>
            <DataTableHeadCell align="right">RS Count</DataTableHeadCell>
            <DataTableHeadCell align="right">DV Count</DataTableHeadCell>
            <DataTableHeadCell align="right">Diff</DataTableHeadCell>
            <DataTableHeadCell>Status</DataTableHeadCell>
          </tr>
        </thead>
        <tbody>
          {data.results.map((r) => (
            <DataTableRow key={r.entity}>
              <DataTableCell bold>{r.entity}</DataTableCell>
              <DataTableCell align="right">{r.rs_total.toLocaleString()}</DataTableCell>
              <DataTableCell align="right">{r.dv_total.toLocaleString()}</DataTableCell>
              <DataTableCell align="right">
                {r.diff !== 0 ? <Chip tone="warning">{r.diff}</Chip> : 0}
              </DataTableCell>
              <DataTableCell>
                <Chip tone={r.status === 'balanced' ? 'success' : 'warning'}>{r.status}</Chip>
              </DataTableCell>
            </DataTableRow>
          ))}
        </tbody>
      </DataTable>
    </DataTableCard>
  );
};
